import { call, put, select } from 'redux-saga/effects';
import localDecisionTreeActions from 'redux/actions/localDecisionTreeActions';
import getQuestionsDecisionTree from 'api/decisionTree';
import decisionTreeCurrentStepActions from 'redux/actions/decisionTreeCurrentStepActions';
import {
  allDTServiceSelector,
  currentDecisionTreeSelector,
  currentDTAnswersSelector,
  currentDTSelector,
  currentDTServiceSelector,
  diagnosisPathSelector,
} from 'redux/selectors/localDecisionTree';
import { DecisionTreeType } from 'ts/types/decision-tree.types';
import { getTernaryOption } from 'utils/app-utils';
import {
  computableContractsIconAdapter,
  isValueInSomeIcons,
} from 'components/Icons/utils';
import {
  getDiagnosisDTContext,
  getProductId,
} from './common-decision-tree-logic-saga';
import { partnerSelector, selectLanguage } from 'redux/selectors/config';
import applicationSpinnerActions from 'redux/actions/applicationSpinnerActions';
import { addErrorAlert } from 'redux/actions/alertActions';

const { startLoad, stopLoad } = applicationSpinnerActions;

const { getNextDTQuestion } = decisionTreeCurrentStepActions;
const {
  isCheckUpAvailableForCurrentDiagnosisPath,
  prepareNewDecisionTreeService,
  updateCurrentIcon,
} = localDecisionTreeActions;

const isResponseStatusEqualTo = (response, status) =>
  response.coverageResponses?.[0]?.result?.global?.status === status;

export function* analyzeDTResponseSaga({ payload: { response } }) {
  const currentDT = yield select(currentDTSelector);
  if (!response.nextQuestion) {
    if (currentDT === DecisionTreeType.DIAGNOSIS) {
      yield analyzeDTDiagnosisResponseSaga({ response });
    } else {
      yield analyzeDTServiceResponseSaga({ response });
    }
  }
  const iconData = yield getCategoryAndIcon();
  if (iconData) {
    yield put(updateCurrentIcon(iconData));
  }
}

export function* consultCheckUpForCurrentDiagnosisTree() {
  const match = ['SERVICES'];
  const contextQuestions = yield getDiagnosisDTContext();
  const currentDTAnswers = yield select(diagnosisPathSelector);
  const state = [...contextQuestions, ...currentDTAnswers].map(
    ({ id, value }) => ({
      id,
      value,
    }),
  );
  const language = yield select(selectLanguage);
  const productId = yield getProductId(DecisionTreeType.DIAGNOSIS);
  const line_ref = 'line_ref';
  const partner_reference = yield select(partnerSelector);

  try {
    yield put(startLoad());
    const { result } = yield call(getQuestionsDecisionTree, {
      language,
      lines: [{ state, line_ref }],
      partner_reference,
      productId,
      match,
    });
    const checkUpServices = result.coverageResponses
      .map(({ query, result }) => ({
        service: query?.SERVICES,
        status: result?.global?.status,
      }))
      .filter(
        ({ service, status }) =>
          service.includes('CHECK_UP') && status === 'COVERED',
      );

    const { updateCheckUpService } = localDecisionTreeActions;
    yield put(
      updateCheckUpService({
        service: checkUpServices[0]?.service,
      }),
    );
  } catch (error) {
    yield put(addErrorAlert(error));
  } finally {
    yield put(stopLoad());
  }
  const services = yield select(allDTServiceSelector);
  yield put(getNextDTQuestion({ answer: services[0], question: 'SERVICES' }));
}

function* analyzeDTDiagnosisResponseSaga({ response }) {
  const { updateDecisionTreeServices } = localDecisionTreeActions;
  const { result, status } =
    response.coverageResponses?.[0]?.result?.global || {};
  const rawServices =
    response.coverageResponses?.[0]?.result?.lines?.[0]?.outputs;
  const shortDescription =
    rawServices
      ?.filter(
        ({ source, type }) =>
          source.includes('PREF') && type === 'PROBLEM_DESCRIPTION',
      )
      .map(({ valueDisplayName }) => valueDisplayName)?.[0] || '';
  const extraData = yield getCategoryAndIcon();
  if (isResponseStatusEqualTo(response, 'COVERED')) {
    const services =
      rawServices
        ?.filter(
          ({ source, type }) => source.includes('PREF') && type === 'SERVICES',
        )
        .map(({ value }) => value) || [];

    if (services.length) {
      const isSoftFix = services.includes('SOFTFIX');
      const interventionObjects = rawServices.find(
        ({ type }) => type === 'INTERVENTION_OBJECTS',
      );

      const interventionObjectsContext = getTernaryOption(
        interventionObjects,
        [{ id: interventionObjects?.type, value: interventionObjects?.value }],
        [],
      );
      yield put(
        updateDecisionTreeServices({
          services,
          interventionObjectsContext,
          result: {
            shortDescription,
            ...extraData,
            ...result,
            isSoftFix,
            status,
            questions: response.questions,
          },
        }),
      );
      yield put(isCheckUpAvailableForCurrentDiagnosisPath());
    } else {
      yield put(
        updateDecisionTreeServices({
          services: [],
          result: {
            shortDescription,
            ...extraData,
            ...result,
            status,
            questions: response.questions,
          },
        }),
      );
      yield finishIfPossible();
    }
  } else {
    yield put(
      updateDecisionTreeServices({
        services: [],
        result: {
          shortDescription,
          ...extraData,
          ...result,
          questions: response.questions,
          status,
        },
      }),
    );
    yield finishIfPossible();
  }
}

function* finishIfPossible() {
  const { finishDecisionTree } = localDecisionTreeActions;
  yield put(finishDecisionTree());
}

const setIcon = (metadata, id) => {
  let toReturn = ' ';
  const iconValue = metadata
    ? metadata?.find(({ key }) => key === 'icon')?.value
    : computableContractsIconAdapter[id];
  if (iconValue && isValueInSomeIcons(iconValue)) {
    toReturn = iconValue;
  }
  return toReturn;
};

export function* getCategoryAndIcon(idIcon) {
  const diagnosisTree = yield select(currentDecisionTreeSelector);
  const currentPath = yield select(currentDTAnswersSelector);
  const firstQuestionIndex = currentPath.findIndex(({ id }) =>
    id.includes('FIRST_QUESTION'),
  );
  const partialPath = currentPath.slice(0, firstQuestionIndex + 1);
  const firstQuestion = partialPath.slice(-1)[0];
  const firstQuestionStep = partialPath
    .slice(0, -1)
    .map(({ value }) => value)
    .reduce((acc, cur) => acc.givenAnswers[cur], diagnosisTree);
  if (firstQuestion) {
    const { id, displayName, metadata } =
      firstQuestionStep.nextQuestion.answers.find(
        ({ id }) => id === firstQuestion?.value,
      );
    return {
      category: displayName,
      icon: setIcon(metadata, id),
    };
  } else if (idIcon) {
    const previousStepWithIcon = firstQuestionStep.nextQuestion?.answers?.find(
      ({ id }) => id === idIcon,
    );
    if (!previousStepWithIcon) {
      return null;
    }
    const { id, displayName, metadata } = previousStepWithIcon;
    return {
      category: displayName,
      icon: setIcon(metadata, id),
    };
  }
}

function* analyzeDTServiceResponseSaga({ response: { questions } }) {
  const currentService = yield select(currentDTServiceSelector);
  const allServices = yield select(allDTServiceSelector);
  const index = allServices.findIndex(service => service === currentService);
  const service = allServices[index + 1];
  if (service) {
    yield put(prepareNewDecisionTreeService({ questions, service }));
    yield put(getNextDTQuestion({ answer: service, question: 'SERVICES' }));
  } else {
    yield put(prepareNewDecisionTreeService({ questions, service: '' }));
    yield finishIfPossible();
  }
}
